/**
 * Shared Link Detail Page
 *
 * Displays analytics for a link or account shared with the current user.
 * View-only access - no edit capabilities.
 *
 * @module pages/shared-detail
 */

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ExternalLink,
  MousePointerClick,
  Link2,
  Loader2,
  AlertCircle,
  User,
  Eye,
  Calendar,
  Folder,
} from 'lucide-react';
import { apiFetch } from '@/client/lib/api';
import { formatNumber, formatDate } from '@/client/lib/utils';

interface SharedLinkAnalytics {
  shareType: 'link';
  owner: { name: string; email: string };
  link: {
    id: string;
    shortCode: string;
    shortUrl: string;
    destinationUrl: string;
    title: string | null;
    totalClicks: number;
    createdAt: string;
  };
}

interface SharedAccountAnalytics {
  shareType: 'account';
  owner: { name: string; email: string };
  summary: {
    totalLinks: number;
    totalClicks: number;
  };
  links: Array<{
    id: string;
    shortCode: string;
    shortUrl: string;
    title: string | null;
    totalClicks: number;
    createdAt: string;
  }>;
}

interface SharedFolderAnalytics {
  shareType: 'folder';
  owner: { name: string; email: string };
  folder: {
    id: string;
    name: string;
    color: string;
  };
  summary: {
    totalLinks: number;
    totalClicks: number;
  };
  links: Array<{
    id: string;
    shortCode: string;
    shortUrl: string;
    title: string | null;
    totalClicks: number;
    createdAt: string;
  }>;
}

type SharedAnalytics = SharedLinkAnalytics | SharedAccountAnalytics | SharedFolderAnalytics;

export function SharedDetailPage() {
  const { shareId } = useParams<{ shareId: string }>();

  const { data, isLoading, error } = useQuery<SharedAnalytics>({
    queryKey: ['shared', shareId],
    queryFn: async () => {
      const response = await apiFetch(`/api/shares/${shareId}/analytics`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to load shared analytics');
      }
      return response.json();
    },
    enabled: !!shareId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link
          to="/shared"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to shared
        </Link>
        <div className="rounded-lg border bg-card p-12 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-muted-foreground">
            {(error as Error).message || 'You do not have access to view this shared content.'}
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link
          to="/shared"
          className="p-2.5 rounded-xl border bg-card hover:bg-muted text-muted-foreground hover:text-foreground transition-all mt-1"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary flex items-center gap-1">
              <Eye className="h-3 w-3" />
              Shared View
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {data.shareType === 'link'
              ? data.link.title || `arml.ink/${data.link.shortCode}`
              : data.shareType === 'folder'
                ? data.folder.name
                : `${data.owner.name || data.owner.email}'s Dashboard`}
          </h1>
          <div className="flex items-center gap-2 text-muted-foreground mt-1">
            <User className="h-4 w-4" />
            <span>Shared by {data.owner.name || data.owner.email}</span>
          </div>
        </div>
      </div>

      {data.shareType === 'link' ? (
        <LinkAnalytics analytics={data} />
      ) : data.shareType === 'folder' ? (
        <FolderAnalytics analytics={data} />
      ) : (
        <AccountAnalytics analytics={data} />
      )}
    </div>
  );
}

function LinkAnalytics({ analytics }: { analytics: SharedLinkAnalytics }) {
  const { link, owner } = analytics;

  return (
    <div className="space-y-6">
      {/* Link Info Card */}
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Link2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{link.title || 'Untitled Link'}</h2>
              <a
                href={link.shortUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm"
              >
                {link.shortUrl}
              </a>
              <p className="text-sm text-muted-foreground mt-1 truncate max-w-lg">
                {link.destinationUrl}
              </p>
            </div>
          </div>
          <a
            href={link.shortUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2.5 rounded-xl border bg-card hover:bg-muted transition-all shrink-0"
          >
            <ExternalLink className="h-5 w-5" />
          </a>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          icon={MousePointerClick}
          label="Total Clicks"
          value={formatNumber(link.totalClicks)}
          color="primary"
        />
        <StatCard
          icon={Calendar}
          label="Created"
          value={formatDate(link.createdAt)}
          color="purple"
        />
        <StatCard
          icon={User}
          label="Owner"
          value={owner.name || owner.email.split('@')[0] || 'Unknown'}
          color="blue"
        />
      </div>

      {/* Note */}
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        <p>
          This is a shared view. You can see analytics but cannot edit this link. Contact{' '}
          {owner.name || owner.email} for more details or to request changes.
        </p>
      </div>
    </div>
  );
}

function AccountAnalytics({ analytics }: { analytics: SharedAccountAnalytics }) {
  const { owner, summary, links } = analytics;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          icon={Link2}
          label="Total Links"
          value={formatNumber(summary.totalLinks)}
          color="primary"
        />
        <StatCard
          icon={MousePointerClick}
          label="Total Clicks"
          value={formatNumber(summary.totalClicks)}
          color="green"
        />
        <StatCard
          icon={User}
          label="Owner"
          value={owner.name || owner.email.split('@')[0] || 'Unknown'}
          color="blue"
        />
      </div>

      {/* Links List */}
      <div className="rounded-lg border bg-card">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Top Performing Links</h2>
          <p className="text-sm text-muted-foreground">
            Showing top {links.length} links by click count
          </p>
        </div>
        <div className="divide-y">
          {links.map((link, index) => (
            <div key={link.id} className="p-4 flex items-center gap-4">
              <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium shrink-0">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{link.title || `arml.ink/${link.shortCode}`}</p>
                <a
                  href={link.shortUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  {link.shortUrl}
                </a>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold">{formatNumber(link.totalClicks)}</p>
                <p className="text-xs text-muted-foreground">clicks</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        <p>
          This is a shared view of {owner.name || owner.email}&apos;s dashboard. You can see
          analytics but cannot edit any links.
        </p>
      </div>
    </div>
  );
}

function FolderAnalytics({ analytics }: { analytics: SharedFolderAnalytics }) {
  const { owner, folder, summary, links } = analytics;

  return (
    <div className="space-y-6">
      {/* Folder Info Card */}
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: folder.color ? `${folder.color}20` : undefined }}
          >
            <Folder className="h-6 w-6" style={{ color: folder.color || undefined }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{folder.name}</h2>
            <p className="text-sm text-muted-foreground">
              Folder shared by {owner.name || owner.email}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          icon={Link2}
          label="Links in Folder"
          value={formatNumber(summary.totalLinks)}
          color="primary"
        />
        <StatCard
          icon={MousePointerClick}
          label="Total Clicks"
          value={formatNumber(summary.totalClicks)}
          color="green"
        />
        <StatCard
          icon={User}
          label="Owner"
          value={owner.name || owner.email.split('@')[0] || 'Unknown'}
          color="blue"
        />
      </div>

      {/* Links List */}
      <div className="rounded-lg border bg-card">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Links in Folder</h2>
          <p className="text-sm text-muted-foreground">
            {links.length} {links.length === 1 ? 'link' : 'links'} sorted by click count
          </p>
        </div>
        <div className="divide-y">
          {links.map((link, index) => (
            <div key={link.id} className="p-4 flex items-center gap-4">
              <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium shrink-0">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{link.title || `arml.ink/${link.shortCode}`}</p>
                <a
                  href={link.shortUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  {link.shortUrl}
                </a>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold">{formatNumber(link.totalClicks)}</p>
                <p className="text-xs text-muted-foreground">clicks</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        <p>
          This is a shared view of the &quot;{folder.name}&quot; folder. You can see analytics but
          cannot edit any links. Contact {owner.name || owner.email} for changes.
        </p>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: 'primary' | 'green' | 'purple' | 'blue';
}) {
  const colorClasses = {
    primary: 'from-primary/20 to-primary/5 text-primary',
    green: 'from-[hsl(152,69%,40%)]/20 to-[hsl(152,69%,40%)]/5 text-[hsl(152,69%,40%)]',
    purple: 'from-[hsl(262,83%,58%)]/20 to-[hsl(262,83%,58%)]/5 text-[hsl(262,83%,58%)]',
    blue: 'from-[hsl(217,91%,60%)]/20 to-[hsl(217,91%,60%)]/5 text-[hsl(217,91%,60%)]',
  };

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex items-center gap-4">
        <div
          className={`h-12 w-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}
        >
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold tabular-nums">{value}</p>
        </div>
      </div>
    </div>
  );
}
